/* =========================================================================
   Arch countdown — shared controller

   Drives BOTH:
     • the `countdown` block on [Arch Main] Banner (main-default-banner)
     • the full-width `arch-countdown-banner` section

   Contract — any element with [data-arch-countdown] and a [data-deadline]:
     [data-cd-days] [data-cd-hours] [data-cd-mins] [data-cd-secs]  ← digit targets
     [data-cd-live]      ← shown while running   (hidden by CSS when expired)
     [data-cd-expired]   ← shown once expired    (hidden by CSS while running)
   Expiry is signalled by toggling `.is-expired` on the root. Liquid also
   renders the correct initial state server-side, so a page that loads after
   the deadline never flashes the live timer; this script only corrects it
   (which matters when Shopify serves a cached page from before the deadline).

   Deliberately vanilla + plain `defer` (no jQuery, not lazyload2): global-arch.js
   is jQuery-dependent and jQuery is NOT loaded in the theme editor, so anything
   routed through it is dead in the customiser. This runs everywhere.
   ========================================================================= */
(function () {
  var SEC = 1000, MIN = 60 * SEC, HOUR = 60 * MIN, DAY = 24 * HOUR;
  var timers = [];
  var ticking = null;

  function pad(n) { return n < 10 ? '0' + n : String(n); }

  // Compact single-line renderings for the Countdown CTA face, e.g. "16d 16:16".
  // The days segment is dropped once it hits 0 rather than printing "0d".
  function compactText(fmt, d, h, m, s) {
    var dSeg = d > 0 ? d + 'd ' : '';
    switch (fmt) {
      case 'd_hhmmss': return dSeg + pad(h) + ':' + pad(m) + ':' + pad(s);
      case 'dhm':      return (d > 0 ? d + 'd ' : '') + h + 'h ' + m + 'm';
      case 'dhms':     return (d > 0 ? d + 'd ' : '') + h + 'h ' + m + 'm ' + s + 's';
      case 'hhmmss':   return (d * 24 + h) + ':' + pad(m) + ':' + pad(s);
      case 'd_hhmm':
      default:         return dSeg + pad(h) + ':' + pad(m);
    }
  }

  function setDigits(t, ms) {
    var d = Math.floor(ms / DAY);
    var h = Math.floor((ms % DAY) / HOUR);
    var m = Math.floor((ms % HOUR) / MIN);
    var s = Math.floor((ms % MIN) / SEC);
    // Days can exceed 99 on a long lead-in — pad to 2 but never truncate.
    if (t.days)  t.days.textContent  = pad(d);
    if (t.hours) t.hours.textContent = pad(h);
    if (t.mins)  t.mins.textContent  = pad(m);
    if (t.secs)  t.secs.textContent  = pad(s);
    if (t.compact) t.compact.textContent = compactText(t.format, d, h, m, s);
  }

  function expire(t) {
    if (t.expired) return;
    t.expired = true;
    t.root.classList.add('is-expired');
    setDigits(t, 0);
    // Announce the swap once, to the expired panel only — a per-second aria-live
    // region would spam screen readers (WCAG 4.1.3 done sanely).
    if (t.expiredEl && !t.expiredEl.hasAttribute('aria-live')) {
      t.expiredEl.setAttribute('aria-live', 'polite');
    }
  }

  function update(t) {
    if (t.expired) return;
    var ms = t.deadline - Date.now();
    if (ms <= 0) { expire(t); return; }
    setDigits(t, ms);
  }

  function tick() {
    var alive = false;
    for (var i = 0; i < timers.length; i++) {
      update(timers[i]);
      if (!timers[i].expired) alive = true;
    }
    // Nothing left to count — stop the interval rather than spin forever.
    if (!alive && ticking) { clearInterval(ticking); ticking = null; }
  }

  function start() {
    if (ticking) return;
    ticking = setInterval(tick, SEC);
  }

  function init(root) {
    if (!root || root.dataset.cdInit === '1') return;

    // The deadline is an ABSOLUTE instant resolved server-side in the shop's
    // timezone (data-deadline-ts, epoch ms) — never the visitor's wall clock.
    // data-deadline (ISO + offset) is only a fallback for when Liquid couldn't
    // parse the merchant's input. Both are timezone-explicit, so a visitor in
    // any zone counts down to the same moment.
    var ts = parseInt(root.getAttribute('data-deadline-ts'), 10);
    var deadline = (isFinite(ts) && ts > 0) ? ts : NaN;
    var raw = root.getAttribute('data-deadline');

    if (isNaN(deadline) && raw) {
      var parsed = new Date(raw).getTime();
      if (!isNaN(parsed)) deadline = parsed;
    }

    if (isNaN(deadline)) {
      // No/undecipherable deadline: fail to the expired (CTA) state rather than
      // showing a frozen 00:00:00:00, and say why in the console for the builder.
      root.classList.add('is-expired', 'cd-no-deadline');
      root.dataset.cdInit = '1';
      if (window.console) {
        console.warn('[arch-countdown] missing/invalid deadline — ts:', ts, 'iso:', raw, root);
      }
      return;
    }

    var t = {
      root: root,
      deadline: deadline,
      expired: false,
      // Big DAYS/HRS/MIN/SEC face (full-width Countdown Banner section)
      days:  root.querySelector('[data-cd-days]'),
      hours: root.querySelector('[data-cd-hours]'),
      mins:  root.querySelector('[data-cd-mins]'),
      secs:  root.querySelector('[data-cd-secs]'),
      // Compact inline face (Countdown CTA block) — one of the two is present
      compact: root.querySelector('[data-cd-compact]'),
      format: root.getAttribute('data-cd-format') || 'd_hhmm',
      expiredEl: root.querySelector('[data-cd-expired]')
    };

    root.dataset.cdInit = '1';
    timers.push(t);
    update(t);
    if (!t.expired) start();
  }

  function scan(scope) {
    var nodes = (scope || document).querySelectorAll('[data-arch-countdown]');
    for (var i = 0; i < nodes.length; i++) init(nodes[i]);
  }

  // Long-idle tabs throttle setInterval, so the clock drifts. Recompute on
  // return instead of trusting the tick count.
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) tick();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }

  // Theme editor: Shopify replaces the section's DOM on every settings change,
  // which drops our init flag along with the old nodes. Re-scan the new subtree.
  document.addEventListener('shopify:section:load', function (e) {
    scan(e.target);
  });
  document.addEventListener('shopify:block:select', function (e) {
    scan(e.target);
  });
})();
